import React, { useState, useEffect } from 'react';
import {
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Fab,
  Stack,
  Paper,
  Typography,
  Box,
  TextareaAutosize,
  Button
} from '@mui/material';
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from '@mui/icons-material/Cancel';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { fetchSelectOptions, validateFile, currentDate } from '../../../utils/formUtils';

const UpdateFormField = ({ structure, id, values, setValues, showPaypalFields, category = null }) => {

  const [selectOptions, setSelectOptions] = useState([]);
  const [filePreview, setFilePreview] = useState(null);
  const [multipleTexts, setMultipleTexts] = useState([]);
  const [value, setValue] = useState(values[structure.field] || '');

  useEffect(() => {
    if (['select', 'select2', 'select9'].includes(structure.inputType)) {
      fetchSelectOptions("getcategory", structure.data || structure.data4 || structure.data9)
        .then(options => setSelectOptions(options));
    }
  }, [structure.inputType, structure.data, structure.data4, structure.data9]);

  const getCategoryPrefix = (category) => {
    const prefixMap = {
      companyPresentations: 'company_presentation',
      jobAdvertisements: 'job_advertisement',
      flyers: 'flyer',
      menus: 'menu',
      infos: 'info',
      events: 'event',
      advertisingVideos: 'advertising_video'
    };
    return prefixMap[category] || category;
  };

  // Helper to convert structure field to data field
  const getDataField = (structureField, category) => {
    const prefix = getCategoryPrefix(`${category}s`);

    // Special case mappings
    const specialMappings = {
      [`${category}`]: 'photo', // Main photo/file field
      [`${category}StartDay`]: 'startDay',
      [`${category}EndDay`]: 'endDay',
      [`${category == 'companyPresentation' ? 'company':category}WelcomeStartDay`]: 'welcomeStartDay',
      [`${category == 'companyPresentation' ? 'company':category}WelcomeEndDay`]: 'welcomeEndDay',
      [`${prefix}_feed_show_status`]: 'feed_show_status',
      [`${prefix}_subject_line`]: 'subject_line',
      [`${prefix}_input_text`]: 'input_text',
      [`${prefix}_picture_1`]: 'picture_1',
      [`${prefix}_picture_2`]: 'picture_2',
      [`${prefix}_like_count`]: 'like_count',
      [`${prefix}_wishlist_count`]: 'wishlist_count'
    };
    console.log("🚀 ~ getDataField ~ specialMappings:", specialMappings)

    return specialMappings[structureField];
  };

  useEffect(() => {
    if (category) {
      const posts = values.posts?.[`${category}s`] || [];
      const post = posts.find(post => post._id === id);
      if (post) {
        
        const dataField = getDataField(structure.field, category);
       
        setValue(post[dataField] || '');
        if (structure.inputType === 'file' && post[dataField]) {
          setFilePreview(`${post[dataField]?.filename}`);
        }
      }
    } else {
      setValue(values[structure.field] || '');
      if (structure.inputType === 'file' && values[structure.field]) {
      }
    }
  }, []);

  const handleChange = (event) => {
    const { name, value, checked, type, files } = event.target;
    let newValue = type === 'checkbox' ? checked : value;
  
    if (type === 'file') {
      const file = files[0];
      if (validateFile(file, structure.fileType, 36700165)) {
        setValues(prev => ({ ...prev, [name]: file }));
        setFilePreview(URL.createObjectURL(file));
      }
      return;
    }
  
    setValue(newValue);
  
    setValues(prev => {
      const updatedValues = { ...prev, [name]: newValue };
  
      if (category) {
        const dataField = getDataField(name, category);
        const postsArray = [...(prev.posts?.[`${category}s`] || [])];
  
        const postIndex = postsArray.findIndex(post => post._id === id);
        if (postIndex !== -1) {
          postsArray[postIndex] = {
            ...postsArray[postIndex],
            [dataField]: newValue
          };
        }
  
        updatedValues.posts = {
          ...prev.posts,
          [`${category}`]: postsArray
        };
      }
  
      return updatedValues;
    });
    console.log("🚀 ~ handleChange ~ setValues:", values)
  };
  

  const handleMultipleTextChange = (index, value) => {
    const newTexts = [...multipleTexts];
    newTexts[index] = value;
    setMultipleTexts(newTexts);
    setValues(prev => ({ ...prev, [structure.field]: newTexts }));
  };

  const addMultipleText = () => {
    if (multipleTexts.length < 10) {
      setMultipleTexts([...multipleTexts, '']);
    } else {
      alert('Maximum of 10 texts only');
    }
  };

  const removeMultipleText = (index) => {
    const newTexts = multipleTexts.filter((_, i) => i !== index);
    setMultipleTexts(newTexts);
    setValues(prev => ({ ...prev, [structure.field]: newTexts }));
  };

  if (
    ['paypalClientId', 'paypalClientSecret'].includes(structure.field) &&
    !showPaypalFields
  ) {
    return null;
  }

  const commonStyles = {
    marginBottom: 2,
    '& .MuiInputBase-root': {
      borderRadius: 1
    }
  };

  switch (structure.inputType) {
    case 'text':
    case 'date':
      return (
        <TextField
          fullWidth
          size="small"
          id={`${id}_${structure.field}`}
          name={structure.field}
          label={structure.headerName}
          type={structure.inputType}
          value={value}
          onChange={handleChange}
          disabled={structure.isDisabled}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={structure.inputType === 'date' ? { min: currentDate } : {}}
          sx={commonStyles}
        />
      );
    case 'switch':
      return (
        <FormControlLabel
          control={
            <Switch
              checked={value}
              onChange={handleChange}
              name={structure.field}
              size="small"
            />
          }
          label={structure.headerName}
        />
      );
    case 'select':
    case 'select2':
    case 'select9':
      return (
        <FormControl fullWidth size="small" sx={commonStyles}>
          <InputLabel>{structure.headerName}</InputLabel>
          <Select
            value={value}
            onChange={handleChange}
            name={structure.field}
            label={structure.headerName}
          >
            {selectOptions.map((option, index) => (
              <MenuItem key={index} value={option.Name}>{option.Name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case 'file':
    case 'videoFile':
    case 'pdfFile':
      return (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <label htmlFor={`${id}_${structure.field}`}>
              <input
                style={{ display: 'none' }}
                id={`${id}_${structure.field}`}
                name={structure.field}
                type="file"
                onChange={handleChange}
                accept={structure.fileType.join(',')}
              />
              <Fab
                color="secondary"
                size="small"
                component="span"
                aria-label="add"
                variant="extended"
              >
                <AddIcon /> {structure.headerName}
              </Fab>
            </label>
            {filePreview && (
              <Box sx={{ width: 100, height: 100, overflow: 'hidden' }}>
                <img src={filePreview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            )}
            {values[structure.field] && <Typography variant="body2">{values[structure.field].name}</Typography>}
          </Stack>
        </Box>
      );
    case 'multiText':
      return (
        <TextareaAutosize
          id={`${id}_${structure.field}`}
          name={structure.field}
          placeholder={structure.headerName}
          minRows={4}
          style={{
            width: '100%',
            padding: '8px',
            marginBottom: '16px',
            borderRadius: '4px',
            borderColor: 'rgba(0, 0, 0, 0.23)',
            fontSize: '14px'
          }}
          value={value}
          onChange={handleChange}
        />
      );
    case 'multipleText':
      return (
        <Box sx={{ mb: 2 }}>
          {multipleTexts.map((text, index) => (
            <Stack key={index} direction="row" spacing={1} sx={{ mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={text}
                onChange={(e) => handleMultipleTextChange(index, e.target.value)}
                label={`${structure.headerName} ${index + 1}`}
              />
              <IconButton onClick={() => removeMultipleText(index)} size="small">
                <CancelIcon />
              </IconButton>
            </Stack>
          ))}
          {multipleTexts.length < 10 && (
            <Button
              startIcon={<AddIcon />}
              onClick={addMultipleText}
              variant="outlined"
              size="small"
            >
              Add {structure.headerName}
            </Button>
          )}
        </Box>
      );
    case 'heading':
      return <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{structure.title}</Typography>;
    default:
      return null;
  }
};

export default UpdateFormField;
